import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useI18n, langPath } from "@/i18n/I18nContext";
import { X, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "zynteroq_consent_v2";

const DEFAULT_STATE = {
  necessary: true,
  analytics: false,
  ads: false,
  personalization: false,
};

function pushConsent(state) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  const gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  gtag("consent", "update", {
    ad_storage: state.ads ? "granted" : "denied",
    ad_user_data: state.ads ? "granted" : "denied",
    ad_personalization: state.ads ? "granted" : "denied",
    analytics_storage: state.analytics ? "granted" : "denied",
    personalization_storage: state.personalization ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
  window.dataLayer.push({ event: "cookie_consent_update", consent: state });
}

export function useCookieBanner() {
  const [visible, setVisible] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_STATE);
  const [showManage, setShowManage] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setVisible(true);
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        setPrefs({ ...DEFAULT_STATE, ...parsed });
        // Re-apply consent silently on load
        pushConsent({ ...DEFAULT_STATE, ...parsed });
      } else {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }
  }, []);

  const open = useCallback(() => {
    setShowManage(true);
    setVisible(true);
  }, []);

  return { visible, setVisible, prefs, setPrefs, showManage, setShowManage, open };
}

export default function CookieBanner({ state }) {
  const { t, lang } = useI18n();
  const { visible, setVisible, prefs, setPrefs, showManage, setShowManage } = state;

  if (!visible) return null;

  const save = (next) => {
    setPrefs(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch (e) {}
    pushConsent(next);
    setVisible(false);
    setShowManage(false);
  };

  const acceptAll = () => save({ necessary: true, analytics: true, ads: true, personalization: true });
  const rejectAll = () => save({ necessary: true, analytics: false, ads: false, personalization: false });
  const savePrefs = () => save({ ...prefs, necessary: true });

  return (
    <div
      role="dialog"
      aria-label={t("cookie.title")}
      className="fixed inset-x-4 bottom-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[440px] z-50 z-cookie-enter"
      data-testid="cookie-banner"
    >
      <div className="rounded-2xl shadow-2xl border overflow-hidden" style={{ background: "#0E1B1A", color: "#F5F1EA", borderColor: "rgba(255,255,255,0.06)" }}>
        {!showManage ? (
          <div className="p-5">
            <div className="flex items-start gap-3">
              <span className="inline-flex w-9 h-9 rounded-lg items-center justify-center" style={{ background: "rgba(231,201,137,0.14)" }}>
                <ShieldCheck size={18} color="#E7C989"/>
              </span>
              <div className="flex-1">
                <h3 className="font-display text-[18px] leading-tight">{t("cookie.title")}</h3>
                <p className="mt-2 text-[13px] leading-relaxed opacity-80">
                  {t("cookie.text")}{" "}
                  <Link to={langPath(lang, "/cookies")} className="underline underline-offset-4">
                    {t("cookie.readMore")}
                  </Link>.
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={acceptAll} className="z-btn z-btn-gold flex-1" data-testid="cookie-accept">
                {t("cookie.accept")}
              </button>
              <button type="button" onClick={rejectAll} className="z-btn flex-1" style={{ background: "rgba(255,255,255,0.08)", color: "#F5F1EA" }} data-testid="cookie-reject">
                {t("cookie.reject")}
              </button>
            </div>
            <button type="button" onClick={() => setShowManage(true)} className="mt-3 text-[12px] underline underline-offset-4 opacity-80" data-testid="cookie-manage">
              {t("cookie.manage")}
            </button>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-[18px]">{t("cookie.manage")}</h3>
              <button type="button" onClick={() => setShowManage(false)} className="opacity-70 hover:opacity-100" aria-label={t("cookie.back")}>
                <X size={18}/>
              </button>
            </div>
            <div className="mt-4 space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {[
                { key: "necessary", locked: true },
                { key: "analytics" },
                { key: "ads" },
                { key: "personalization" },
              ].map((row) => (
                <div key={row.key} className="rounded-xl p-3 border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.04)" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[14px] font-semibold">{t(`cookie.${row.key}`)}</p>
                      <p className="text-[12px] opacity-75 mt-1">{t(`cookie.${row.key}Text`)}</p>
                    </div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={row.locked ? true : !!prefs[row.key]}
                        disabled={row.locked}
                        onChange={(e) => setPrefs((p) => ({ ...p, [row.key]: e.target.checked }))}
                        data-testid={`cookie-toggle-${row.key}`}
                      />
                      <span className={`w-10 h-6 flex items-center rounded-full p-0.5 transition-colors ${row.locked ? "bg-emerald-700 opacity-70" : (prefs[row.key] ? "bg-emerald-600" : "bg-white/20")}`}>
                        <span className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${((row.locked || prefs[row.key]) ? "translate-x-4" : "translate-x-0")}`}/>
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={savePrefs} className="z-btn z-btn-gold flex-1" data-testid="cookie-save">
                {t("cookie.save")}
              </button>
              <button type="button" onClick={acceptAll} className="z-btn flex-1" style={{ background: "rgba(255,255,255,0.08)", color: "#F5F1EA" }} data-testid="cookie-accept-2">
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
